import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardBody, Spinner, Button, Badge } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "components/Common/Breadcrumb2";
import { formatDateTimeToAmPm } from "helpers/dateFormat_helper";
import AssignmentSubmissionTable from "./assignmentSubmissionTable";
import { fetchAssignmentDetailsRequest } from "store/assignment/actions";
import { FadeLoader } from "react-spinners";

const AssignmentView = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  // ✅ Local UI states for pagination, search, filters
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchString, setSearchString] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedSortData, setSelectedSortData] = useState(null);

  // ✅ Redux data
  const { selectedAssignment: assignment, submissions, total, loading } = useSelector(
    (state) => state.Assignment
  );

  // ✅ Fetch data via Redux (no direct axios)
  useEffect(() => {
    const params = {
      page: pageIndex,
      limit: pageSize,
      search: searchString,
      status: selectedStatus?.value || "",
    };
    if (selectedSortData?.value && selectedSortData?.direction) {
      params.sortBy = `${selectedSortData.value}:${selectedSortData.direction}`;
    }

    dispatch(fetchAssignmentDetailsRequest(id, params));
  }, [dispatch, id, pageIndex, pageSize, searchString, selectedStatus, selectedSortData]);

  //  Loader
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
       <FadeLoader color="#00a895" size={40} />
      </div>  
    );
  }

  if (!assignment) {
    return (
      <div className="text-center mt-5">
        <h5>No Assignment Found</h5>
      </div>
    );
  }

  return (
    <div className="page-content container-fluid">
      {/* 🔹 Breadcrumb + Back Button */}
      <div className="d-flex justify-content-between align-items-center mx-3 mb-4">
        <Breadcrumb
          title="Assignment Details"
          breadcrumbItems={[
            { title: "Dashboard", link: "/dashboard" },
            { title: "Assignments", link: "/assignments" },
            { title: assignment.title, link: "#" },
          ]}
        />
        <Link to="/assignments">
          <Button color="secondary">Back</Button>
        </Link>
      </div>

      {/* 🔹 Assignment Info */}
      <Card className="shadow-sm border-0 mb-4">
        <CardBody>
          <h4 className="fw-bold mb-2">{assignment.title}</h4>
          <p className="text-muted mb-3" style={{ maxWidth: "700px" }}>
            {assignment.description || "No description available."}
          </p>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <Badge
              className="d-flex align-items-center justify-content-center"
              color={assignment.status === "Active" ? "success" : "danger"}
              pill
            >
              {assignment.status}
            </Badge>
            <span>
              <strong>Deadline:</strong> {formatDateTimeToAmPm(assignment.deadline)}
            </span>
            <span>
              <strong>Total Marks:</strong> {assignment.totalMarks}
            </span>
            <span>
              <strong>Created By:</strong> {assignment?.createdBy?.name || "-"}
            </span>
          </div>

          {/* 🔹 Files Section */}
          {assignment.files?.length > 0 && (
            <div className="mt-4">
              <h6 className="fw-bold mb-2">Attached Files</h6>
              <ul className="list-unstyled">
                {assignment.files.map((file) => (
                  <li key={file._id}>
<a
  href={`https://docs.google.com/gview?url=${encodeURIComponent(file.fileUrl)}&embedded=true`}
  target="_blank"
  rel="noopener noreferrer"
  className="text-primary"
  style={{ textDecoration: "none" }}
>
  📄 {file.name} ({file.size})
</a>

                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 🔹 Assigned Students */}
          {/* {assignment.assignedTo?.length > 0 && (
            <div className="mt-4">
              <h6 className="fw-bold mb-2">Assigned To</h6>
              <ul className="list-unstyled">
                {assignment.assignedTo.map((student) => (
                  <li key={student._id}>
                    👤 {student.name} —{" "}
                    <span className="text-muted">{student.email}</span>
                  </li>
                ))}
              </ul>
            </div>
          )} */}
        </CardBody>
      </Card>

      {/* 🔹 Submissions Table */}
      <Card className="shadow-sm mt-4">
        <CardBody>
          <h5 className="fw-bold mb-3">Submissions</h5>
          <AssignmentSubmissionTable
            submissions={submissions}
            loading={loading}
            totalrows={total || submissions?.length || 0}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            pageSize={pageSize}
            setPageSize={setPageSize}
            searchString={searchString}
            setSearchString={setSearchString}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedSortData={selectedSortData}
            setSelectedSortData={setSelectedSortData}
          />
        </CardBody>
      </Card>
    </div>
  );
};

export default AssignmentView;
